import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  ShieldCheck, Download, Send, Loader2, Users, CalendarClock, Mail, Plus, X, FileSpreadsheet
} from 'lucide-react';

const API = `${window.location.origin}/api`;

export default function InsuranceDeclaration() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/admin/insurance-declaration/settings`, { headers }),
        axios.get(`${API}/admin/insurance-declaration/preview`, { headers }),
      ]);
      setSettings(s.data);
      setPreview(p.data);
    } catch (e) {
      toast.error('Could not load insurance declaration settings');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setField = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));

  const addEmail = () => {
    const e = newEmail.trim();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error('Enter a valid email'); return; }
    if ((settings.recipient_emails || []).some(x => x.toLowerCase() === e.toLowerCase())) { toast.error('Already added'); return; }
    setField('recipient_emails', [...(settings.recipient_emails || []), e]);
    setNewEmail('');
  };

  const removeEmail = (e) => setField('recipient_emails', settings.recipient_emails.filter(x => x !== e));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...settings, premium_per_worker: parseFloat(settings.premium_per_worker) || 0 };
      const r = await axios.put(`${API}/admin/insurance-declaration/settings`, payload, { headers });
      setSettings(r.data);
      toast.success('Insurance declaration settings saved');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save settings');
    } finally { setSaving(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/insurance-declaration/download`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `HANDY BAYANI Declaration ${preview?.tomorrow || ''}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download the declaration');
    } finally { setDownloading(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/insurance-declaration/send-now`, {}, { headers });
      toast.success(`Declaration sent to ${(r.data.recipients || []).length} recipient(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the declaration');
    } finally { setSending(false); }
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen bg-stone-50 flex">
        <AdminSidebar />
        <AdminMobileNav />
        <main className="flex-1 md:ml-64 pt-16 md:pt-0 flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        </main>
      </div>
    );
  }

  const field = (label, key, type = 'text', placeholder = '') => (
    <div>
      <Label className="text-sm text-slate-700">{label}</Label>
      <Input type={type} value={settings[key] ?? ''} onChange={(e) => setField(key, e.target.value)} placeholder={placeholder} className="mt-1" data-testid={`ins-${key}`} />
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />
      <main className="flex-1 md:ml-64 overflow-x-hidden pt-16 md:pt-0 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6" data-testid="insurance-declaration-page">        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Insurance Declaration</h1>
            <p className="text-sm text-slate-500">Automatically emails insurers the daily manpower declaration at 11:00 PM (Manila) — workers booked today and serviced tomorrow.</p>
          </div>
        </div>

        {/* Preview card */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4" data-testid="declaration-preview-card">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <CalendarClock className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Service date</p>
                <p className="font-semibold text-slate-900" data-testid="preview-tomorrow">{preview?.tomorrow || '—'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Workers to declare</p>
                <p className="font-semibold text-slate-900" data-testid="preview-count">{preview?.count ?? 0}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total premium</p>
                <p className="font-semibold text-slate-900">{preview?.total_premium != null ? `₱${preview.total_premium.toLocaleString()}` : '—'}</p>
              </div>
            </div>
          </div>

          {preview?.workers?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="preview-workers-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-3">#</th>
                    <th className="py-2 pr-3">Last Name</th>
                    <th className="py-2 pr-3">First Name</th>
                    <th className="py-2 pr-3">Middle</th>
                    <th className="py-2 pr-3">Home Address</th>
                    <th className="py-2 pr-3">Birthday</th>
                    <th className="py-2 pr-3">Beneficiary</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.workers.map((w, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-2 pr-3 text-slate-400">{i + 1}</td>
                      <td className="py-2 pr-3">{w.last_name || <span className="text-red-400">—</span>}</td>
                      <td className="py-2 pr-3">{w.first_name || <span className="text-red-400">—</span>}</td>
                      <td className="py-2 pr-3">{w.middle_name || '—'}</td>
                      <td className="py-2 pr-3">{w.present_home_address || <span className="text-red-400">missing</span>}</td>
                      <td className="py-2 pr-3">{w.birthday || <span className="text-red-400">missing</span>}</td>
                      <td className="py-2 pr-3">{w.beneficiary_name || <span className="text-red-400">missing</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview?.count === 0 && (
            <p className="mt-4 text-sm text-slate-400">No bookings were created today for service tomorrow. Nothing will be sent tonight.</p>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="outline" onClick={download} disabled={downloading} data-testid="download-declaration-btn">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Download .xlsx
            </Button>
            <Button onClick={sendNow} disabled={sending} className="bg-blue-600 hover:bg-blue-700" data-testid="send-now-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Generate & send now
            </Button>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4 space-y-4" data-testid="declaration-settings-card">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-slate-900">Assured & Insurer Settings</h2>
            <button
              onClick={() => setField('enabled', !settings.enabled)}
              role="switch" aria-checked={settings.enabled}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${settings.enabled ? 'bg-emerald-500' : 'bg-stone-300'}`}
              data-testid="declaration-enabled-toggle"
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${settings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <p className="text-xs text-slate-500 -mt-2">When off, the nightly email won't be sent (you can still use "Generate & send now").</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field('Assured Name', 'assured_name', 'text', 'HANDY BAYANI')}
            {field('TIN', 'tin')}
            {field('Address', 'address')}
            {field('Contact Person', 'contact_person')}
            {field('Designated Position', 'designated_position')}
            {field('Contact Number', 'contact_number')}
            {field('Option', 'option')}
            {field('Premium per worker (₱)', 'premium_per_worker', 'number', '0')}
          </div>

          {/* Recipients */}
          <div>
            <Label className="text-sm text-slate-700 flex items-center gap-1.5"><Mail className="w-4 h-4" /> Insurer recipient emails</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {(settings.recipient_emails || []).length === 0 && (
                <span className="text-xs text-slate-400">No recipients yet. Add at least one insurer email.</span>
              )}
              {(settings.recipient_emails || []).map((e) => (
                <span key={e} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-blue-50 text-blue-700 text-sm rounded-full border border-blue-200" data-testid={`recipient-${e}`}>
                  {e}
                  <button onClick={() => removeEmail(e)} className="rounded-full hover:bg-blue-200 p-0.5" data-testid={`remove-recipient-${e}`}><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                placeholder="insurer@company.com" data-testid="recipient-email-input"
              />
              <Button variant="outline" onClick={addEmail} data-testid="add-recipient-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="pt-2">
            <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-declaration-settings-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save settings
            </Button>
          </div>
        </div>
      </div>
      </main>
    </div>
  );
}
